import React, { useEffect, useState } from 'react';
import { loadStripe, PaymentIntent, StripeElementsOptions } from "@stripe/stripe-js";

import { Elements, useStripe } from '@stripe/react-stripe-js';
import { ProfileQL } from '../../../models/Profile';
import { BaseReact } from '../../../base.model';
import SectionTitleBar from '../../components/titlebar';
import { numberToCurrencyString } from '../../../services/formatting';
import { STRIPE_PUBLISHABLE_KEY } from '../../../stripeKeys';
import './PaymentIntent.complete.scss';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

interface PaymentIntentCompleteProps extends BaseReact {
    currentUser: ProfileQL;
}

interface PaymentIntentCompleteStatusProps {
    paymentIntentSecret?: string;
}

const SuccessIcon =
    <svg width="22" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M15.4695 0.232963C15.8241 0.561287 15.8454 1.1149 15.5171 1.46949L6.14206 11.5945C5.97228 11.7778 5.73221 11.8799 5.48237 11.8748C5.23253 11.8698 4.99677 11.7582 4.83452 11.5681L0.459523 6.44311C0.145767 6.07557 0.18937 5.52327 0.556912 5.20951C0.924454 4.89575 1.47676 4.93936 1.79051 5.3069L5.52658 9.68343L14.233 0.280522C14.5613 -0.0740672 15.1149 -0.0953599 15.4695 0.232963Z" fill="white" />
    </svg>;

const ErrorIcon =
    <svg width="22" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M1.25628 1.25628C1.59799 0.914573 2.15201 0.914573 2.49372 1.25628L8 6.76256L13.5063 1.25628C13.848 0.914573 14.402 0.914573 14.7437 1.25628C15.0854 1.59799 15.0854 2.15201 14.7437 2.49372L9.23744 8L14.7437 13.5063C15.0854 13.848 15.0854 14.402 14.7437 14.7437C14.402 15.0854 13.848 15.0854 13.5063 14.7437L8 9.23744L2.49372 14.7437C2.15201 15.0854 1.59799 15.0854 1.25628 14.7437C0.914573 14.402 0.914573 13.848 1.25628 13.5063L6.76256 8L1.25628 2.49372C0.914573 2.15201 0.914573 1.59799 1.25628 1.25628Z" fill="white" />
    </svg>;

const InfoIcon =
    <svg width="22" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M10 1.5H4C2.61929 1.5 1.5 2.61929 1.5 4V10C1.5 11.3807 2.61929 12.5 4 12.5H10C11.3807 12.5 12.5 11.3807 12.5 10V4C12.5 2.61929 11.3807 1.5 10 1.5ZM4 0C1.79086 0 0 1.79086 0 4V10C0 12.2091 1.79086 14 4 14H10C12.2091 14 14 12.2091 14 10V4C14 1.79086 12.2091 0 10 0H4Z" fill="white" />
        <path fillRule="evenodd" clipRule="evenodd" d="M5.25 7C5.25 6.58579 5.58579 6.25 6 6.25H7.25C7.66421 6.25 8 6.58579 8 7V10.5C8 10.9142 7.66421 11.25 7.25 11.25C6.83579 11.25 6.5 10.9142 6.5 10.5V7.75H6C5.58579 7.75 5.25 7.41421 5.25 7Z" fill="white" />
        <path d="M5.75 4C5.75 3.31075 6.31075 2.75 7 2.75C7.68925 2.75 8.25 3.31075 8.25 4C8.25 4.68925 7.68925 5.25 7 5.25C6.31075 5.25 5.75 4.68925 5.75 4Z" fill="white" />
    </svg>;

const STATUS_CONTENT_MAP = {
    succeeded: {
        text: "Pago confirmado exitosamente.",
        subtext: 'Tu saldo se actualizará automáticamente, la factura de tu pago será generada usando los datos de facturación de tu emisor.',
        iconClass: "successIcon",
        icon: SuccessIcon,
    },
    processing: {
        text: "Tu pago está siendo procesado.",
        subtext: 'Tu saldo se actualizará automáticamente una vez se confirme el pago, para cualquier aclaración guarda el recibo de tu pago y comunícate con soporte.',
        iconClass: "infoIcon",
        icon: InfoIcon,
    },
    requires_payment_method: {
        text: "Tu pago no fue exitoso, por favor intenta nuevamente.",
        subtext: 'Por favor verifica los datos de tu método de pago e intenta nuevamente, si tienes algún problema usando pagos con tarjeta intenta otro método de pago como transferencia SPEI.',
        iconClass: "errorIcon",
        icon: ErrorIcon,
    },
    requires_action: {
        text: "Tu pago requiere una acción adicional.",
        subtext: 'Por favor completa la acción requerida, en caso de transferencias SPEI recibirás un correo con las instrucciones de pago que deben ser concluidas.',
        iconClass: "infoIcon",
        icon: InfoIcon,
    },
    canceled: {
        text: "Algo salió mal, por favor intenta nuevamente.",
        subtext: 'Por favor intenta nuevamente.',
        iconClass: "errorIcon",
        icon: ErrorIcon,
    },
    default: {
        text: "Algo salió mal, por favor intenta nuevamente.",
        subtext: 'Por favor intenta nuevamente.',
        iconClass: "errorIcon",
        icon: ErrorIcon,
    }
};

function PaymentIntentCompleteStatus({
    paymentIntentSecret
}: PaymentIntentCompleteStatusProps) {
    const stripe = useStripe();

    const [status, setStatus] = useState<keyof typeof STATUS_CONTENT_MAP | 'default'>('default');
    const [intent, setIntent] = useState<PaymentIntent | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!stripe) return;
        if (!paymentIntentSecret) return;

        stripe.retrievePaymentIntent(paymentIntentSecret).then(({ paymentIntent }) => {
            if (!paymentIntent) {
                return;
            }

            console.log('Retrieved PaymentIntent: ', paymentIntent);
            if (paymentIntent.status in STATUS_CONTENT_MAP) {
                setStatus(paymentIntent.status as any);
            } else {
                setStatus('default');
            }

            setIntent(paymentIntent);
            setIsLoading(false);
        });
    }, [paymentIntentSecret, stripe])

    if (isLoading) {
        return (
            <div id="PaymentIntentCompleteStatus">
                <h2>Cargando estado del pago...</h2>
            </div>
        )
    }

    return (
        <div id="PaymentIntentCompleteStatus">
            <div className='paymentStatus card'>
                <div
                    className={`statusIcon ${STATUS_CONTENT_MAP[status].iconClass}`}
                >
                    {STATUS_CONTENT_MAP[status].icon}
                </div>
                <div className='statusText'>
                    <h3>{STATUS_CONTENT_MAP[status].text}</h3>
                    <p>{STATUS_CONTENT_MAP[status].subtext}</p>
                </div>
            </div>
            {
                intent && (
                    <div className='paymentDetails card'>
                        <h4>Detalles del Pago</h4>
                        <table className="paymentDetailsTable">
                            <tbody>
                                <tr>
                                    <td className="detailKey">Monto:</td>
                                    <td className="detailValue">{numberToCurrencyString(intent.amount / 100)} {intent.currency.toUpperCase()}</td>
                                </tr>
                                <tr>
                                    <td className="detailKey">ID del Pago:</td>
                                    <td className="detailValue">{intent.id}</td>
                                </tr>
                                <tr>
                                    <td className="detailKey">Método de Pago:</td>
                                    <td className="detailValue">{intent.payment_method as string}</td>
                                </tr>
                                <tr>
                                    <td className="detailKey">Email:</td>
                                    <td className="detailValue">{intent.receipt_email}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )
            }
        </div>
    )
}

function PaymentIntentComplete({
    currentUser
}: PaymentIntentCompleteProps) {
    const query = new URLSearchParams(window.location.search);
    const paymentIntentSecret = query.get('payment_intent_client_secret');

    const options: StripeElementsOptions = {
        appearance: {
            theme: 'stripe' as const,
            variables: {
                colorText: '#4d4f5c',
                colorPrimary: '#ac2e78',
            }
        },
        loader: 'auto' as const,
    };

    return (
        <div id="PaymentIntentComplete">
            <SectionTitleBar currentUser={currentUser} title="Confirmación y estatus de pago" />
            <Elements stripe={stripePromise} options={options}>
                {
                    paymentIntentSecret ?
                        <PaymentIntentCompleteStatus paymentIntentSecret={paymentIntentSecret} />
                        : <p>No se encontró información de pago.</p>
                }
            </Elements>
        </div>
    );
}

export default PaymentIntentComplete;